import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import OrderItemsTable from './OrderItemsTable';
import './OrderForm.css';

const OrderForm = ({
  initialData = {},
  items = [],
  addresses = [],
  addressesLoading = false,
  sessions = [],
  sessionsLoading = false,
  onSubmit,
  onCancel,
  cancelLabel = '← Back to Products',
  onUpdateItem,
  onRemoveItem,
  onUpdatePrice,
  onUpdateShipping,
  shippingChargeDollars = 0,
  isEmployee = false,
  isSubmitting = false,
  creditBalanceCents = 0,
}) => {
  const [formData, setFormData] = useState({
    fulfillment_type: initialData.fulfillment_type || 'delivery',
    special_instructions: initialData.special_instructions || '',
    delivery_address_id: initialData.delivery_address ?? null,
    session_id: initialData.session_id ?? null,
  });

  // Auto-select the first open session once sessions load
  const sessionAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (sessionsLoading || sessionAutoSelectedRef.current) return;
    setFormData((prev) => {
      if (prev.session_id) {
        sessionAutoSelectedRef.current = true;
        return prev;
      }
      const first = sessions[0] ?? null;
      if (first) {
        sessionAutoSelectedRef.current = true;
        return { ...prev, session_id: first.id };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionsLoading, sessions]);

  // Once addresses finish loading, pre-select the default (or first) shipping address.
  // autoSelectedRef ensures we only do this once — it avoids both the stale-closure
  // problem (reading formData inside the effect) and the need to suppress exhaustive-deps.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (addressesLoading || autoSelectedRef.current) return;
    // Only auto-select when no address has been chosen yet (covers initialData too)
    setFormData((prev) => {
      if (prev.delivery_address_id) {
        autoSelectedRef.current = true;
        return prev;
      }
      const defaultAddress = addresses.find((a) => a.is_default) ?? addresses[0] ?? null;
      if (defaultAddress) {
        autoSelectedRef.current = true;
        return { ...prev, delivery_address_id: defaultAddress.id };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressesLoading, addresses]);

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
    }
  };

  const isPickup = formData.fulfillment_type === 'pickup';
  const selectedAddress = addresses.find((a) => a.id === formData.delivery_address_id) ?? null;

  const validate = () => {
    const newErrors = {};
    if (items.length === 0) {
      newErrors.items = 'At least one item is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (onSubmit) onSubmit({ ...formData, items });
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <form className="order-form" onSubmit={handleSubmit}>
      <div className="order-form__layout">

        {/* ── Left: Order Summary ──────────────────────────── */}
        <div className="order-form__summary">
          <div className="order-form__summary-header">
            <span className="order-form__section-label">Order Summary</span>
            <span className="order-form__item-count">
              {totalItems} {totalItems === 1 ? 'unit' : 'units'} · {items.length} {items.length === 1 ? 'product' : 'products'}
            </span>
          </div>

          {errors.items && (
            <div className="order-form__error">{errors.items}</div>
          )}

          <OrderItemsTable
            items={items}
            editable={true}
            isEmployee={isEmployee}
            onUpdateQuantity={onUpdateItem}
            onUpdatePrice={onUpdatePrice}
            onUpdateShipping={onUpdateShipping}
            onRemoveItem={onRemoveItem}
            shippingChargeDollars={shippingChargeDollars}
            creditBalanceCents={creditBalanceCents}
          />
        </div>

        {/* ── Right: Delivery Details ───────────────────────── */}
        <div className="order-form__delivery">
          <div className="order-form__section-label">Delivery Details</div>

          {/* Fulfillment type toggle */}
          <div className="order-form__field">
            <div className="order-form__fulfillment-toggle" role="group" aria-label="Fulfillment type">
              <button
                type="button"
                className={`order-form__fulfillment-btn${!isPickup ? ' order-form__fulfillment-btn--active' : ''}`}
                onClick={() => handleChange('fulfillment_type', 'delivery')}
              >
                Delivery / Shipping
              </button>
              <button
                type="button"
                className={`order-form__fulfillment-btn${isPickup ? ' order-form__fulfillment-btn--active' : ''}`}
                onClick={() => handleChange('fulfillment_type', 'pickup')}
              >
                Pickup at Mill
              </button>
            </div>
          </div>

          {/* Delivery / Shipping info */}
          {!isPickup && (
            <div className="order-form__delivery-info">
              <p className="order-form__delivery-info-heading">How it works</p>
              <p className="order-form__delivery-info-body">
                Orders of $200 or more are hand-delivered on scheduled routes to the New River Valley, Roanoke Valley, Shenandoah Valley, Piedmont Triad, Lynchburg, Charlottesville, Richmond, Fredericksburg, DC Area, and select locations in southwest Virginia, western North Carolina, and eastern and central Tennessee.
              </p>
              <p className="order-form__delivery-info-body">
                Orders under $200 ship via UPS. Delivery fee or shipping cost varies by location.
              </p>
            </div>
          )}

          {/* Mill Session selector */}
          <div className="order-form__field">
            <label className="order-form__label" htmlFor="session_id">
              Mill Session
            </label>
            {sessionsLoading ? (
              <p className="order-form__loading">Loading sessions…</p>
            ) : sessions.length === 0 ? (
              <p className="order-form__hint">No open sessions available.</p>
            ) : (
              <>
                <select
                  id="session_id"
                  className="order-form__select"
                  value={formData.session_id ?? ''}
                  onChange={(e) => handleChange('session_id', e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">— Select session —</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
                {(() => {
                  const selected = sessions.find((s) => s.id === formData.session_id);
                  if (!selected) return null;
                  const fmt = (str) => {
                    const [y, m, d] = str.split('-').map(Number);
                    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    });
                  };
                  return (
                    <div className="order-form__session-detail">
                      <div className="order-form__session-detail-row">
                        <span className="order-form__session-detail-label">Order by</span>
                        <span className="order-form__session-detail-value">{fmt(selected.mill_start)}</span>
                      </div>
                      <div className="order-form__session-detail-row">
                        <span className="order-form__session-detail-label">Mill dates</span>
                        <span className="order-form__session-detail-value">{fmt(selected.mill_start)} – {fmt(selected.mill_end)}</span>
                      </div>
                      <div className="order-form__session-detail-row">
                        <span className="order-form__session-detail-label">Delivery begins</span>
                        <span className="order-form__session-detail-value">{fmt(selected.delivery_start)}</span>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>

          {/* Shipping address selector — hidden for pickup */}
          {!isPickup && (
            <div className="order-form__field">
              <label className="order-form__label" htmlFor="delivery_address_id">
                Delivery Address
              </label>
              {addressesLoading ? (
                <p className="order-form__loading">Loading addresses…</p>
              ) : addresses.length === 0 ? (
                <p className="order-form__hint">No shipping addresses on file.</p>
              ) : (
                <div className={`order-form__address-box${selectedAddress?.notes ? ' order-form__address-box--has-notes' : ''}`}>
                  <select
                    id="delivery_address_id"
                    className="order-form__address-select"
                    value={formData.delivery_address_id ?? ''}
                    onChange={(e) => handleChange('delivery_address_id', e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">— Select address —</option>
                    {addresses.map((addr) => (
                      <option key={addr.id} value={addr.id}>
                        {addr.street_address}{addr.street_address_2 ? `, ${addr.street_address_2}` : ''}, {addr.city}, {addr.state} {addr.zip_code}
                        {addr.is_default ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                  {selectedAddress?.notes && (
                    <div className="order-form__address-notes">
                      <span className="order-form__address-notes-label">Notes</span>
                      {selectedAddress.notes}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Pickup notice */}
          {isPickup && (
            <p className="order-form__pickup-notice">
              You'll pick up this order at the mill. We'll contact you when it's ready.
            </p>
          )}

          <div className="order-form__field">
            <label className="order-form__label" htmlFor="special_instructions">
              Special Instructions
              <span className="order-form__label-optional"> — optional</span>
            </label>
            <textarea
              id="special_instructions"
              className="order-form__textarea"
              value={formData.special_instructions}
              onChange={(e) => handleChange('special_instructions', e.target.value)}
              placeholder="Any notes for your order…"
              rows={4}
            />
          </div>

          <div className="order-form__actions">
            <button
              type="submit"
              className="order-form__submit"
              disabled={isSubmitting || items.length === 0}
            >
              {isSubmitting ? 'Placing Order…' : 'Place Order'}
              {!isSubmitting && <span className="order-form__submit-arrow">→</span>}
            </button>

            {onCancel && (
              <button
                type="button"
                className="order-form__cancel"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {cancelLabel}
              </button>
            )}
          </div>
        </div>

      </div>
    </form>
  );
};

OrderForm.propTypes = {
  initialData: PropTypes.object,
  items: PropTypes.array,
  addresses: PropTypes.array,
  addressesLoading: PropTypes.bool,
  sessions: PropTypes.array,
  sessionsLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  cancelLabel: PropTypes.string,
  onUpdateItem: PropTypes.func,
  onRemoveItem: PropTypes.func,
  onUpdatePrice: PropTypes.func,
  onUpdateShipping: PropTypes.func,
  shippingChargeDollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isEmployee: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  creditBalanceCents: PropTypes.number,
};

export default OrderForm;
